
import React, { useState, useEffect } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Box,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BarChartIcon from '@mui/icons-material/BarChart';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTranslation } from 'react-i18next';
import SettingsModal from './SettingsModal/SettingsModal';
import StatisticsModal from './StatisticsModal';
import AchievementsModal from './AchievementsModal';
import useTeamColors from '../hooks/useTeamColors';

// Hook to detect mobile devices
const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            // Detect touch devices or small screens
            const isTouchDevice = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
            const isSmallScreen = window.innerWidth <= 1024;
            setIsMobile(isTouchDevice || isSmallScreen);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    return isMobile;
};

// Retro flip-board style score digit component
const ScoreDigit = ({ value }) => (
    <Box sx={{
        background: 'linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 45%, #1a1a1a 55%, #0d0d0d 100%)',
        borderRadius: '6px',
        padding: { xs: '2px 6px', sm: '4px 10px' },
        minWidth: { xs: '24px', sm: '32px' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 2px 4px rgba(0,0,0,0.5), 0 4px 8px rgba(0,0,0,0.3)',
        border: '1px solid #0a0a0a',
        position: 'relative',
        // Horizontal split line effect
        '&::after': {
            content: '""',
            position: 'absolute',
            left: '2px',
            right: '2px',
            top: '50%',
            height: '1px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.8) 20%, rgba(0,0,0,0.8) 80%, transparent 100%)',
        }
    }}>
        <Typography sx={{
            fontFamily: '"Courier New", Courier, monospace',
            fontWeight: 'bold',
            fontSize: { xs: '1rem', sm: '1.4rem' },
            color: '#f5f5f0',
            textShadow: '0 0 8px rgba(255,255,255,0.3), 0 2px 4px rgba(0,0,0,0.8)',
            letterSpacing: '-1px',
        }}>
            {value}
        </Typography>
    </Box>
);

// Retro scoreboard component
const RetroScoreboard = ({ team1Score, team2Score, players }) => {
    const { teamColors } = useTeamColors();
    
    // Get team names from players (Team 1: players 0 and 2, Team 2: players 1 and 3)
    const team1Name = players && players.length >= 3
        ? `${players[0]?.name || 'Jugador'} y ${players[2]?.name || 'Compañero'}`
        : 'Pareja 1';
    const team2Name = players && players.length >= 4
        ? `${players[1]?.name || 'Rival Der.'} y ${players[3]?.name || 'Rival Izq.'}`
        : 'Pareja 2';

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1 },
            background: 'linear-gradient(180deg, #3d3d3d 0%, #2a2a2a 100%)',
            borderRadius: '10px',
            padding: { xs: '4px 8px', sm: '6px 14px' },
            boxShadow: '0 4px 12px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
            border: '2px solid #1a1a1a',
        }}>
            {/* Team 1 Label */}
            <Typography sx={{
                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                fontWeight: 'bold',
                color: teamColors.team1.main,
                letterSpacing: '0.3px',
                textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                whiteSpace: 'nowrap',
            }}>
                {team1Name}
            </Typography>

            {/* Team 1 Score */}
            <ScoreDigit value={team1Score} />

            {/* Separator */}
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                mx: 0.5,
            }}>
                <Box sx={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: '#ffd700',
                    boxShadow: '0 0 6px rgba(255,215,0,0.6)',
                }} />
                <Box sx={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: '#ffd700',
                    boxShadow: '0 0 6px rgba(255,215,0,0.6)',
                }} />
            </Box>

            {/* Team 2 Score */}
            <ScoreDigit value={team2Score} />

            {/* Team 2 Label */}
            <Typography sx={{
                fontSize: '0.75rem',
                fontWeight: 'bold',
                color: teamColors.team2.main,
                letterSpacing: '0.3px',
                textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                whiteSpace: 'nowrap',
            }}>
                {team2Name}
            </Typography>
        </Box>
    );
};

const Header = ({ title, showSettings = true, player, dealer, onBack, globalScore, players, isDealing = false, onInGameSettings, onChangelog, onReset }) => {
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [statsOpen, setStatsOpen] = useState(false);
    const [achievementsOpen, setAchievementsOpen] = useState(false);
    const { t } = useTranslation();
    const isMobile = useIsMobile();

    return (
        <>
            <Box sx={{ flexGrow: 1 }}>
                <AppBar position="static">
                    <Toolbar sx={{ position: 'relative', minHeight: { xs: '48px', sm: '56px' }, py: { xs: 0.5, sm: 1 } }}>
                        {/* Left section: Back button and title */}
                        <Box sx={{ display: 'flex', alignItems: 'center', zIndex: 1 }}>
                            {onBack && (
                                <IconButton
                                    color="inherit"
                                    onClick={onBack}
                                    aria-label="back"
                                    size="large"
                                    edge="start"
                                    sx={{ mr: { xs: 0.5, sm: 1 }, p: { xs: 0.75, sm: 1 } }}
                                >
                                    <ArrowBackIcon sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' } }} />
                                </IconButton>
                            )}

                            <Typography variant="h6" component="div" sx={{ whiteSpace: 'nowrap', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                {title || t('app.title')}
                            </Typography>
                        </Box>

                        {/* Center section: Global Score - Absolutely positioned */}
                        {globalScore && (
                            <Box sx={{
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 0,
                            }}>
                                <RetroScoreboard
                                    team1Score={globalScore.team1}
                                    team2Score={globalScore.team2}
                                    players={players}
                                />
                            </Box>
                        )}

                        {/* Right section: Dealer, Turn info and Settings */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            ml: 'auto',
                            zIndex: 1,
                        }}>
                            {/* Dealer and Turn info - On mobile devices, alternate based on isDealing */}
                            {/* Dealer info - shown on desktop always, on mobile only when dealing */}
                            {dealer && (!isMobile || isDealing) && (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mr: 1,
                                }}>
                                    <Typography variant="caption" sx={{ mr: 0.5, opacity: 0.8 }}>
                                        Reparte:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 0.5 }}>
                                        {dealer.name}
                                    </Typography>
                                    <Typography sx={{ fontSize: '1.2rem', mr: player ? 1 : 0 }}>
                                        {dealer.avatar}
                                    </Typography>
                                    {/* Separator - only shown on desktop */}
                                    {player && !isMobile && (
                                        <Typography sx={{
                                            mx: 0.5,
                                            opacity: 0.4,
                                        }}>|</Typography>
                                    )}
                                </Box>
                            )}

                            {/* Turn info - shown on desktop always, on mobile only when NOT dealing */}
                            {player && (!isMobile || !isDealing) && (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mr: 1,
                                }}>
                                    <Typography variant="caption" sx={{ mr: 0.5, opacity: 0.8 }}>
                                        Turno:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 0.5 }}>
                                        {player.name}
                                    </Typography>
                                    <Typography sx={{ fontSize: '1.2rem' }}>
                                        {player.avatar}
                                    </Typography>
                                </Box>
                            )}

                            {/* In-game settings button - shown when onInGameSettings is provided */}
                            {onInGameSettings && (
                                <IconButton
                                    color="inherit"
                                    onClick={onInGameSettings}
                                    aria-label="game settings"
                                    size="large"
                                    sx={{ p: { xs: 0.75, sm: 1 } }}
                                >
                                    <MoreVertIcon sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' } }} />
                                </IconButton>
                            )}

                            {/* Reset button for GameSetup */}
                            {onReset && (
                                <IconButton
                                    color="inherit"
                                    onClick={onReset}
                                    aria-label="reset"
                                    size="large"
                                    sx={{ p: { xs: 0.75, sm: 1 } }}
                                >
                                    <RestartAltIcon sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' } }} />
                                </IconButton>
                            )}

                            {/* Changelog button for Home */}
                            {onChangelog && (
                                <IconButton
                                    color="inherit"
                                    onClick={onChangelog}
                                    aria-label="changelog"
                                    size="large"
                                    sx={{ p: { xs: 0.75, sm: 1 } }}
                                >
                                    <NewReleasesIcon sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' } }} />
                                </IconButton>
                            )}

                            {showSettings && (
                                <>
                                    <IconButton
                                        color="inherit"
                                        onClick={() => setAchievementsOpen(true)}
                                        aria-label="achievements"
                                        size="large"
                                        sx={{ p: { xs: 0.75, sm: 1 } }}
                                    >
                                        <EmojiEventsIcon sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' } }} />
                                    </IconButton>
                                    <IconButton
                                        color="inherit"
                                        onClick={() => setStatsOpen(true)}
                                        aria-label="statistics"
                                        size="large"
                                        sx={{ p: { xs: 0.75, sm: 1 } }}
                                    >
                                        <BarChartIcon sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' } }} />
                                    </IconButton>
                                    <IconButton
                                        color="inherit"
                                        onClick={() => setSettingsOpen(true)}
                                        aria-label="settings"
                                        size="large"
                                        sx={{ p: { xs: 0.75, sm: 1 } }}
                                    >
                                        <SettingsIcon sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' } }} />
                                    </IconButton>
                                </>
                            )}
                        </Box>
                    </Toolbar>
                </AppBar>
            </Box>
            <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
            <StatisticsModal open={statsOpen} onClose={() => setStatsOpen(false)} />
            <AchievementsModal open={achievementsOpen} onClose={() => setAchievementsOpen(false)} />
        </>
    );
};

export default Header;
